import classnames from 'classnames/bind'
import {
  forwardRef,
  ForwardRefRenderFunction,
  ReactChild,
  ReactNode,
  useCallback,
  useImperativeHandle,
  useRef,
} from 'react'
import { useTranslate } from 'react-polyglot'
import { GlobalTextPreset } from '~/@types/text-preset'
import { GlobalTextStyling } from '~/@types/text-styling'

import { BigArrowLeftIcon } from '~/components/Icons'
import { ClosePopinIcon } from '~/components/UI/Icons'
import InlineCta, { InlineCtaProps } from '~/components/UI/InlineCta'

import { usePopIn } from '~/providers/PopInProvider'
import { useStyle } from '~/providers/StyleProvider'

import { CTA } from '~/data/dictionary'

import css from './styles.module.scss'

const cx = classnames.bind(css)

export interface PopInCloseIconProps {
  className?: string
  withPrev?: boolean
  onClick?(): void
}

export const PopInCloseIcon = ({
  className,
  withPrev = false,
  onClick,
}: PopInCloseIconProps) => {
  const { removeCurrent, removeAll } = usePopIn()

  const t = useTranslate()

  const processedOnClick = () => {
    if (withPrev) {
      removeAll()
    } else {
      removeCurrent()
    }
    onClick?.()
  }

  return (
    <button
      className={cx(className, css.PopInCloseIcon)}
      aria-label={t(CTA.CLOSE)}
      onClick={processedOnClick}>
      <ClosePopinIcon />
    </button>
  )
}

export const PopInPrevIcon = ({ className, onClick }: PopInCloseIconProps) => {
  const { removeCurrent } = usePopIn()

  const processedOnClick = () => {
    removeCurrent()
    onClick?.()
  }

  return (
    <BigArrowLeftIcon
      className={cx(className, css.PopInPrevIcon)}
      onClick={processedOnClick}
    />
  )
}
export interface customCloseProps {
  className?: string
  props?: InlineCtaProps
}

export const CustomClose = ({ className, props }: customCloseProps) => {
  const { removeCurrent } = usePopIn()
  const { className: customClassName, ...rest } = props

  const processedOnClick = useCallback(() => {
    removeCurrent()
  }, [removeCurrent])

  return (
    <InlineCta
      className={cx(className, customClassName)}
      onClick={processedOnClick}
      {...rest}
    />
  )
}

export interface PopInProps {
  className?: string
  children?: ReactChild[] | ReactChild
  isCloseButton?: boolean
  withPrev?: boolean
  customCloseButton?: InlineCtaProps
  closeClassName?: string
  prevClassName?: string
  popinHeader?: ReactNode
}

const PopIn: ForwardRefRenderFunction<HTMLDivElement, PopInProps> = (
  {
    className,
    children,
    isCloseButton = true,
    withPrev = false,
    popinHeader,
    closeClassName,
    prevClassName,
    customCloseButton,
  },
  ref,
) => {
  const headerStyle = useStyle({
    textPreset: GlobalTextPreset.Label12Primary,
    textStyling: GlobalTextStyling.UpperCase,
  })
  const popInRef = useRef(null)

  useImperativeHandle(ref, () => popInRef.current)

  return (
    <div ref={popInRef} className={cx(className, css.PopIn)}>
      <div className={cx(css.PopInHeader, { withPrev })}>
        {withPrev && <PopInPrevIcon className={cx(prevClassName)} />}
        {popinHeader && (
          <div className={cx(css.left, headerStyle)}>{popinHeader}</div>
        )}

        {isCloseButton && !customCloseButton && (
          <PopInCloseIcon className={cx(closeClassName)} withPrev />
        )}
      </div>

      {children}
    </div>
  )
}

export default forwardRef<HTMLDivElement, PopInProps>(PopIn)
